import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, Save, Trash2 } from 'lucide-react';

import PageHeader from '../components/PageHeader.jsx';
import SaveBar, { SaveButton } from '../components/SaveBar.jsx';
import FormField, { TextInput } from '../components/FormField.jsx';
import SearchableSelect from '../components/SearchableSelect.jsx';
import { listBatches } from '../api/batches';
import { listProducts } from '../api/products';
import { getSale, updateSale } from '../api/sales';
import { VAT_RATE } from '../constants/vat';

/**
 * Edit Sale — line items only (per Simon's scope: customer, dates, notes,
 * and shipping stay locked on mobile; if you need to change those, the
 * desktop ERP is the right place).
 *
 * Reachable from SaleDetail's "Edit line items" action when sale is draft.
 * Backend gates PATCH /sales/{id} to draft-only — if the sale has moved
 * on between page-load and submit we surface the 400 inline rather than
 * silently failing.
 *
 * Pre-fill flow:
 *   1. GET /sales/:id on mount.
 *   2. Build the picker `lines` state from sale.line_items, mapping each
 *      item's product_id/product_name into a minimal product object the
 *      SearchableSelect understands.
 *   3. For each line, async-fetch the product's in-stock batches so the
 *      user can change batches if they want. The line's existing batch_id
 *      is preserved as the selected option even if the batch no longer
 *      has stock — backend will catch any mismatch on save.
 *
 * On save we send only `{ line_items }` — a partial PATCH leaves the rest
 * of the sale (customer, notes, shipping, dates) untouched.
 */
export default function EditSale() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Initial sale load — only used to pre-fill the form. We don't store
  // the full sale in state because the form only edits line_items, and
  // the backend returns the fresh sale on save (which we hand off to
  // SaleDetail via the navigate flash).
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(null); // for header

  const [lines, setLines] = useState([]); // see addLine for shape
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Synchronous double-tap guard — closes the gap between tap and
  // the `submitting` state update where a second tap could fire.
  const submitInFlight = useRef(false);

  // Tiny helper that wraps a promise so we can avoid setting state after
  // unmount. The lifecycle for this page is short (load → edit → save →
  // navigate) so the cancellation guard is mostly defensive.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    getSale(id)
      .then(async (sale) => {
        if (cancelled) return;
        if (sale.status !== 'draft') {
          // Bounce back — only draft sales are editable. Use replace so
          // back from the detail page goes wherever the user came from
          // (sales list, etc.) instead of looping back here.
          navigate(`/sales/${sale.id}`, {
            replace: true,
            state: { flash: 'Only draft sales can be edited.' },
          });
          return;
        }
        setInvoiceNumber(sale.invoice_number ?? `Sale #${sale.id}`);

        // Build initial lines. We optimistically set batches=[] +
        // batchesLoading=true; the per-line batch fetch below populates
        // them in parallel.
        const initial = (sale.line_items || []).map((li, idx) => ({
          tempId:         `${li.id}-${idx}`,
          product: {
            id:   li.product_id,
            name: li.product_name ?? `Product #${li.product_id}`,
            // Show the batch reference inline so the rep knows which
            // batch this line currently draws from before the picker
            // populates with alternatives.
            sublabel: li.batch_order ? `Currently batch ${li.batch_order}` : null,
          },
          quantity:       String(li.quantity ?? ''),
          unit_price:     String(li.unit_price ?? ''),
          batches:        [],
          batchId:        li.batch_id ?? null,
          batchesLoading: true,
          batchError:     null,
        }));
        if (cancelled) return;
        setLines(initial);
        setLoading(false);

        // Fetch batches per line in parallel. Each line resolves
        // independently — slow ones don't block the rest.
        await Promise.all(initial.map(async (line) => {
          try {
            const batches = await listBatches({
              productId: line.product.id,
              hasStock:  true,
            });
            // FIFO: oldest batch first (lowest id).
            const sorted = [...batches].sort((a, b) => a.id - b.id);

            // Preserve the line's existing batch even if it's no longer
            // returned by hasStock=true (e.g. fully drawn down by
            // another sale in the meantime). Worst case, backend
            // rejects on save with a clear message.
            const haveCurrent = sorted.some((b) => b.id === line.batchId);
            const merged = haveCurrent
              ? sorted
              : (line.batchId
                  ? [{ id: line.batchId, order_number: '(current)', available_qty: 0 }, ...sorted]
                  : sorted);

            if (cancelled) return;
            setLines((curr) =>
              curr.map((l) =>
                l.tempId === line.tempId
                  ? {
                      ...l,
                      batches:        merged,
                      // Don't re-default the batch — keep whatever the
                      // sale was already using. Only fall back to FIFO
                      // first if the line had no batch (shouldn't
                      // happen in practice — backend requires batch_id).
                      batchId:        l.batchId ?? sorted[0]?.id ?? null,
                      batchesLoading: false,
                      batchError:     sorted.length === 0 && !haveCurrent ? 'No stock available' : null,
                    }
                  : l,
              ),
            );
          } catch (err) {
            if (cancelled) return;
            setLines((curr) =>
              curr.map((l) =>
                l.tempId === line.tempId
                  ? {
                      ...l,
                      batchesLoading: false,
                      batchError:     err.response?.data?.detail || 'Failed to load batches',
                    }
                  : l,
              ),
            );
          }
        }));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.response?.data?.detail || err.message || 'Failed to load sale');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [id, navigate]);

  // ── Product picker for the "+ Add product" row at the bottom ──────────
  const loadProductOptions = useCallback(async (q) => {
    const data = await listProducts({ pageSize: 30, search: q || undefined });
    return data.items.map((p) => ({
      id:   p.id,
      name: p.name,
      sublabel: [p.sku, p.total_stock != null ? `${p.total_stock} in stock` : null]
        .filter(Boolean)
        .join(' · '),
      total_stock: p.total_stock,
    }));
  }, []);

  // ── addLine: same flow as NewSale's addLine (FIFO auto-select on a
  // newly-picked product). Pulled in verbatim so the two pages stay in
  // sync visually + behaviourally. ───────────────────────────────────────
  async function addLine(product) {
    const tempId = Date.now() + Math.random();
    setLines((curr) => [
      ...curr,
      {
        tempId,
        product,
        quantity:       '1',
        unit_price:     '0',
        batches:        [],
        batchId:        null,
        batchesLoading: true,
        batchError:     null,
      },
    ]);
    try {
      const batches = await listBatches({ productId: product.id, hasStock: true });
      const sorted = [...batches].sort((a, b) => a.id - b.id);
      setLines((curr) =>
        curr.map((l) =>
          l.tempId === tempId
            ? {
                ...l,
                batches:        sorted,
                batchId:        sorted[0]?.id ?? null,
                batchesLoading: false,
                batchError:     sorted.length === 0 ? 'No stock available for this product' : null,
              }
            : l,
        ),
      );
    } catch (err) {
      setLines((curr) =>
        curr.map((l) =>
          l.tempId === tempId
            ? {
                ...l,
                batchesLoading: false,
                batchError:     err.response?.data?.detail || 'Failed to load batches',
              }
            : l,
        ),
      );
    }
  }

  function updateLine(tempId, patch) {
    setLines((curr) => curr.map((l) => (l.tempId === tempId ? { ...l, ...patch } : l)));
  }

  function removeLine(tempId) {
    setLines((curr) => curr.filter((l) => l.tempId !== tempId));
  }

  // ── Totals — VAT-exclusive subtotal, VAT, grand total. Shipping isn't
  // edited on this page; the backend leaves the existing shipping_amount
  // alone when we PATCH only line_items, so the user's preview here is
  // line-items-only. The detail page's totals (post-save) reflect the
  // real picture including shipping. ────────────────────────────────────
  const lineSubtotal = lines.reduce(
    (acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0),
    0,
  );
  const vat = lineSubtotal * VAT_RATE;
  const total = lineSubtotal + vat;

  async function onSubmit(e) {
    e.preventDefault();
    if (submitInFlight.current) return;
    if (lines.length === 0) {
      setError('Add at least one line item.');
      return;
    }
    // Quantity must be > 0 — backend rejects zero/negative qtys but with
    // a vague message; catching it here gives a clearer in-context error
    // and avoids a wasted round-trip. Number("") === 0 so empty inputs
    // are caught by the same check.
    const linesBadQty = lines.filter((l) => !(Number(l.quantity) > 0));
    if (linesBadQty.length > 0) {
      setError('Every line needs a quantity greater than zero.');
      return;
    }
    const linesMissingBatch = lines.filter((l) => !l.batchId);
    if (linesMissingBatch.length > 0) {
      const stillLoading = linesMissingBatch.some((l) => l.batchesLoading);
      setError(
        stillLoading
          ? 'Still loading batch info — give it a moment then try again.'
          : 'Pick a batch for every line item.',
      );
      return;
    }

    submitInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        line_items: lines.map((l) => ({
          product_id: l.product.id,
          batch_id:   l.batchId,
          quantity:   Number(l.quantity),
          unit_price: Number(l.unit_price),
        })),
      };
      const updated = await updateSale(id, payload);
      navigate(`/sales/${updated.id}`, {
        replace: true,
        state: {
          flash: `Line items updated · ${lines.length} item${lines.length === 1 ? '' : 's'}`,
        },
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save changes');
    } finally {
      setSubmitting(false);
      submitInFlight.current = false;
    }
  }

  if (loading) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Edit Sale" icon={Pencil} />
        <p className="text-small text-gray-500">Loading…</p>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Edit Sale" icon={Pencil} />
        <p role="alert" className="text-small text-danger">{loadError}</p>
      </main>
    );
  }

  return (
    <main className="min-h-full px-4 pb-28">
      <PageHeader
        title={invoiceNumber ?? 'Edit Sale'}
        subtitle="Edit line items"
        icon={Pencil}
      />

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <p className="text-small font-medium text-gray-700 mb-1.5">Line items</p>
          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.tempId} className="mb-card space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-gray-900 truncate">
                      {l.product.name}
                    </p>
                    {l.product.sublabel && (
                      <p className="text-caption text-gray-500 truncate">
                        {l.product.sublabel}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(l.tempId)}
                    aria-label="Remove line"
                    className="min-h-touch min-w-touch flex items-center justify-center text-danger"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <FormField label="Qty">
                    <TextInput
                      type="number"
                      inputMode="decimal"
                      min="1"
                      step="1"
                      value={l.quantity}
                      onChange={(e) => updateLine(l.tempId, { quantity: e.target.value })}
                    />
                  </FormField>
                  <FormField label="Unit price (ex VAT)">
                    <TextInput
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={l.unit_price}
                      onChange={(e) => updateLine(l.tempId, { unit_price: e.target.value })}
                    />
                  </FormField>
                </div>

                {/* Batch picker — same logic as NewSale: single-batch
                    case shows static text rather than a redundant
                    dropdown; multi-batch case lets the rep pick. */}
                {l.batchesLoading ? (
                  <p className="text-caption text-gray-500">Loading batches…</p>
                ) : l.batchError ? (
                  <p className="text-caption text-danger">{l.batchError}</p>
                ) : l.batches.length === 1 ? (
                  <p className="text-caption text-gray-500">
                    Batch:{' '}
                    <span className="font-medium text-gray-700">
                      {l.batches[0].order_number}
                    </span>
                    {' · '}
                    {l.batches[0].available_qty} available
                  </p>
                ) : (
                  <FormField label="Batch">
                    <select
                      value={l.batchId ?? ''}
                      onChange={(e) =>
                        updateLine(l.tempId, { batchId: Number(e.target.value) })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-md text-body bg-surface hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    >
                      {l.batches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.order_number} — {b.available_qty} available
                        </option>
                      ))}
                    </select>
                  </FormField>
                )}

                <p className="text-right text-small text-gray-500">
                  Line total: {((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-2">
            <SearchableSelect
              value={null}
              onChange={addLine}
              placeholder="+ Add product"
              loadOptions={loadProductOptions}
              labelOf={(p) => p.name}
            />
          </div>
        </div>

        {/* Live totals preview — line items only. Existing shipping on
            the sale is preserved by the backend (we PATCH only
            line_items), so the post-save total on SaleDetail will
            differ slightly. We label the preview as "ex shipping" so
            this isn't a surprise. */}
        <section className="mb-card space-y-1">
          <div className="flex justify-between text-small text-gray-600">
            <span>Subtotal (ex VAT, ex shipping)</span>
            <span>{lineSubtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-small text-gray-600">
            <span>VAT (15.5%)</span>
            <span>{vat.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-title font-semibold text-gray-900 pt-1 border-t border-gray-200">
            <span>Total (line items)</span>
            <span>{total.toFixed(2)}</span>
          </div>
        </section>

        {error && <p role="alert" className="text-small text-danger">{error}</p>}

        <SaveBar>
          <SaveButton type="submit" icon={Save} loading={submitting} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save changes'}
          </SaveButton>
        </SaveBar>
      </form>
    </main>
  );
}
